import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Trophy, Star, ArrowLeft, ChevronLeft, ChevronRight, Play, Lock, Medal, Bike, RefreshCw, Undo2, Redo2, Pause, X, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ═══════════════════════════════════════
//  TERRAIN GENERATION (Desert/Canyon)
// ═══════════════════════════════════════
function generateLevelData(level) {
  const points = [];
  const baseY = 310;
  let x = 0;
  const step = 5;

  const flat = (n) => { for (let i = 0; i < n; i++) { points.push({ x, y: baseY }); x += step; } };
  const hill = (w, h) => { for (let i = 0; i <= w; i++) { points.push({ x, y: baseY - Math.sin((i / w) * Math.PI) * h }); x += step; } };
  const rampUp = (w, h) => { for (let i = 0; i <= w; i++) { const t = i / w; points.push({ x, y: baseY - t * t * (3 - 2 * t) * h }); x += step; } };
  const rampDown = (w, h) => { for (let i = 0; i <= w; i++) { const t = i / w; points.push({ x, y: baseY - h + t * t * (3 - 2 * t) * h }); x += step; } };
  const gap = (rW, rH, gW, lH) => {
    rampUp(rW, rH);
    for (let i = 0; i < 3; i++) { points.push({ x, y: baseY + 120 }); x += step; }
    for (let i = 0; i < gW; i++) { points.push({ x, y: baseY + 120 }); x += step; }
    for (let i = 0; i < 3; i++) { points.push({ x, y: baseY - lH + (3 - i) * ((baseY + 120) - (baseY - lH)) / 3 }); x += step; }
    points.push({ x, y: baseY - lH }); x += step;
    rampDown(Math.floor(rW * 0.6), lH);
  };
  const plateau = (rW, h, tW, fW) => { rampUp(rW, h); for (let i = 0; i < tW; i++) { points.push({ x, y: baseY - h }); x += step; } rampDown(fW, h); };
  const rollingHills = (n, aMin, aMax) => { for (let i = 0; i < n; i++) hill(18 + Math.floor(Math.random() * 16), aMin + Math.random() * (aMax - aMin)); };

  flat(45);
  if (level === 1) {
    hill(28, 20); flat(10); hill(24, 28); flat(8);
    rollingHills(3, 10, 25); flat(10);
    hill(32, 32); flat(8); hill(12, 14);
    flat(10); rollingHills(4, 15, 28); flat(10);
    hill(28, 25); flat(15);
  } else if (level === 2) {
    hill(24, 28); flat(8); hill(28, 42); flat(6);
    rollingHills(2, 20, 35); flat(8);
    gap(16, 32, 5, 26); flat(12);
    hill(24, 38); flat(8);
    rollingHills(3, 25, 40); flat(10);
    hill(14, 22); flat(6); hill(18, 30); flat(15);
  } else if (level === 3) {
    hill(20, 32); flat(6);
    gap(15, 38, 7, 30); flat(10);
    rollingHills(2, 25, 42); flat(6);
    gap(16, 42, 8, 34); flat(8);
    plateau(12, 30, 6, 12); flat(6);
    gap(18, 44, 9, 35); flat(10);
    hill(22, 48); flat(8);
    rollingHills(2, 28, 44); flat(15);
  } else if (level === 4) {
    hill(16, 38); flat(5);
    gap(15, 48, 10, 36); flat(6);
    plateau(10, 32, 5, 10); flat(6);
    gap(16, 52, 11, 40); flat(6);
    rollingHills(2, 32, 50); flat(5);
    gap(14, 44, 9, 34); flat(5);
    plateau(8, 38, 4, 8); flat(5);
    gap(16, 48, 10, 38); flat(15);
  } else {
    hill(14, 42); flat(4);
    gap(14, 52, 12, 40); flat(5);
    hill(18, 56);
    gap(13, 48, 10, 36); flat(4);
    plateau(8, 42, 3, 8);
    gap(15, 58, 14, 44); flat(5);
    rollingHills(2, 38, 56); flat(4);
    gap(16, 62, 15, 48); flat(4);
    plateau(7, 46, 3, 7); flat(4);
    gap(14, 54, 12, 42); flat(15);
  }
  flat(55);

  for (let p = 0; p < 3; p++) {
    for (let i = 2; i < points.length - 2; i++) {
      points[i].y = (points[i - 2].y + points[i - 1].y + points[i].y + points[i + 1].y + points[i + 2].y) / 5;
    }
  }

  // Cacti
  const cacti = [];
  for (let i = 10; i < points.length - 10; i += 15 + Math.floor(Math.random() * 25)) {
    const p = points[i];
    if (p.y <= baseY + 5) {
      cacti.push({ x: p.x + (Math.random() - 0.5) * 6, y: p.y, type: Math.random() > 0.4 ? 'tall' : 'short', size: 0.6 + Math.random() * 0.5 });
    }
  }

  // Surface rocks
  const rocks = [];
  for (let i = 8; i < points.length - 8; i += 6 + Math.floor(Math.random() * 12)) {
    const p = points[i];
    if (p.y <= baseY + 5 && Math.random() > 0.4) {
      rocks.push({ x: p.x + (Math.random() - 0.5) * 8, y: p.y, r: 1.5 + Math.random() * 3, shade: Math.random() > 0.5 ? 1 : -1 });
    }
  }

  const finishX = points[points.length - 56]?.x || 5000;
  return { terrain: points, cacti, rocks, finishX };
}

function generateCoins(terrain, level) {
  const coins = [];
  const target = 50;
  const usable = terrain.length - 90;
  const spacing = Math.floor(usable / (target + 1));
  for (let i = 1; i <= target; i++) {
    const idx = Math.min(45 + i * spacing, terrain.length - 56);
    const p = terrain[idx];
    const h = level <= 2 ? 25 + Math.random() * 15 : level === 3 ? 25 + Math.random() * 30 : 25 + Math.random() * 45;
    coins.push({ x: p.x, y: p.y - h, collected: false, id: i });
  }
  return coins;
}

function getTerrainYAt(t, tx) {
  for (let i = 0; i < t.length - 1; i++) {
    if (t[i].x <= tx && t[i + 1].x > tx) {
      const f = (tx - t[i].x) / (t[i + 1].x - t[i].x);
      return t[i].y + f * (t[i + 1].y - t[i].y);
    }
  }
  return t[t.length - 1]?.y || 310;
}
function getTerrainAngleAt(t, tx) {
  for (let i = 0; i < t.length - 1; i++) {
    if (t[i].x <= tx && t[i + 1].x > tx) return Math.atan2(t[i + 1].y - t[i].y, t[i + 1].x - t[i].x);
  }
  return 0;
}

const CLOUDS = Array.from({ length: 10 }, (_, i) => ({ x: i * 260 + Math.random() * 160, y: 12 + Math.random() * 40, s: 0.4 + Math.random() * 0.6 }));

// ═══════════════════════════════════════
//  GAME CANVAS
// ═══════════════════════════════════════
const GameCanvas = ({ level, onFinish, onBack }) => {
  const canvasRef = useRef(null);
  const gameRef = useRef(null);
  const keysRef = useRef({ rotBack: false, rotFwd: false, brake: false, gas: false });
  const frameRef = useRef(null);
  const pausedRef = useRef(false);
  const [endState, setEndState] = useState(null);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    setEndState(null); setPaused(false); pausedRef.current = false;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;
    const ld = generateLevelData(level);
    const coins = generateCoins(ld.terrain, level);

    const game = {
      bike: { x: 100, y: 0, vx: 0, vy: 0, angle: 0, angVel: 0, wheelieTilt: 0, wheelR: 14, onGround: true },
      cam: { x: 0, y: 0 }, terrain: ld.terrain, cacti: ld.cacti, surfaceRocks: ld.rocks,
      coins, finishX: ld.finishX,
      score: 0, coinsCollected: 0, flips: 0, totalRotation: 0, lastAngle: 0,
      time: 0, finished: false, crashed: false, crashType: '',
      particles: [], countdown: 3, countdownTimer: 0, countdownStarted: false,
    };
    game.bike.y = getTerrainYAt(ld.terrain, game.bike.x) - game.bike.wheelR;
    game.bike.angle = getTerrainAngleAt(ld.terrain, game.bike.x);
    game.lastAngle = game.bike.angle;
    gameRef.current = game;
    setTimeout(() => { if (gameRef.current) gameRef.current.countdownStarted = true; }, 200);

    const GRAVITY = 0.24, ACCEL = 0.10, MAX_SPEED = 4.5, BRAKE = 0.91, GFRIC = 0.983;
    const AIR_ROT = 0.038, GND_ROT = 0.016, ACCEL_WHEELIE = 0.002;
    const MAX_WHEELIE = 1.05, MAX_STOPPIE = 0.6;
    const CRASH_ANGLE = Math.PI * 0.38, FLIP_BONUS = 500;

    function addP(px, py, n, color) {
      for (let i = 0; i < n; i++) game.particles.push({
        x: px, y: py, vx: (Math.random() - 0.5) * 6, vy: (Math.random() - 1) * 5,
        life: 1, color, r: 1.5 + Math.random() * 3,
      });
    }
    function endGame(type) {
      if (type === 'finished') {
        game.finished = true;
        game.score += Math.max(0, Math.floor(300 - game.time)) * 10;
        addP(game.bike.x, game.bike.y - 30, 20, '#FFD700');
      } else { game.crashed = true; game.crashType = type; addP(game.bike.x, game.bike.y, 14, '#D4A574'); }
      setEndState({ type: game.finished ? 'finished' : 'crashed', crashType: game.crashType, score: game.score, coins: game.coinsCollected, flips: game.flips, time: game.time });
    }

    // ── UPDATE ──
    function update() {
      if (pausedRef.current) return;
      if (game.countdown >= 0) {
        if (game.countdownStarted) { game.countdownTimer += 1 / 60; if (game.countdownTimer >= 1) { game.countdownTimer = 0; game.countdown--; } }
        return;
      }
      if (game.finished || game.crashed) return;
      const b = game.bike, keys = keysRef.current;
      game.time += 1 / 60;
      b.vy += GRAVITY;
      const groundY = getTerrainYAt(game.terrain, b.x);
      const groundAngle = getTerrainAngleAt(game.terrain, b.x);

      if (b.y + b.wheelR >= groundY) {
        const wasAir = !b.onGround;
        b.y = groundY - b.wheelR; b.onGround = true; b.vy = 0;
        if (wasAir) {
          let diff = b.angle - groundAngle;
          while (diff > Math.PI) diff -= Math.PI * 2;
          while (diff < -Math.PI) diff += Math.PI * 2;
          if (Math.abs(diff) > CRASH_ANGLE) { endGame('landing'); return; }
          const hx = b.x + Math.sin(b.angle) * 42, hy = b.y - Math.cos(b.angle) * 42;
          if (hy >= getTerrainYAt(game.terrain, hx)) { endGame('head'); return; }
          b.angle = groundAngle; b.angVel = 0; b.wheelieTilt = 0;
        }
        // Ground controls
        if (keys.gas) {
          b.vx += ACCEL * Math.cos(groundAngle);
          if (b.vx > MAX_SPEED) b.vx = MAX_SPEED;
          b.wheelieTilt += ACCEL_WHEELIE;
        }
        if (keys.brake) { b.vx *= BRAKE; b.wheelieTilt *= 0.9; }
        if (keys.rotBack) { b.wheelieTilt += GND_ROT; }
        if (keys.rotFwd) { b.wheelieTilt -= GND_ROT; }
        if (!keys.rotBack && !keys.rotFwd && !keys.gas) b.wheelieTilt *= 0.88;
        if (b.wheelieTilt > MAX_WHEELIE) { endGame('wheelie'); return; }
        if (b.wheelieTilt < -MAX_STOPPIE) { endGame('stoppie'); return; }
        b.angle = groundAngle - b.wheelieTilt;
        b.vx *= GFRIC;
        if (b.vx > 1 && Math.random() > 0.65) game.particles.push({ x: b.x - 14, y: b.y + 4, vx: -Math.random() * 1.2, vy: -Math.random() * 1, life: 0.6, color: '#C8A882', r: 1.5 + Math.random() * 2 });
      } else {
        const justLaunched = b.onGround;
        b.onGround = false; b.wheelieTilt = 0;
        if (justLaunched) b.angVel = b.vx * 0.001;
        // Air: rotation buttons ONLY
        if (keys.rotFwd) b.angVel += AIR_ROT;
        if (keys.rotBack) b.angVel -= AIR_ROT;
        if (!keys.rotFwd && !keys.rotBack) b.angVel *= 0.92;
        b.angle += b.angVel;
        b.angVel *= 0.97;
        const rotDiff = b.angle - game.lastAngle;
        game.totalRotation += rotDiff;
        if (Math.abs(game.totalRotation) >= Math.PI * 2) {
          game.flips++; game.score += FLIP_BONUS;
          game.totalRotation %= (Math.PI * 2);
          addP(b.x, b.y - 20, 10, '#FFD700');
        }
        const hx = b.x + Math.sin(b.angle) * 42, hy = b.y - Math.cos(b.angle) * 42;
        if (hy >= getTerrainYAt(game.terrain, hx)) { endGame('head'); return; }
      }
      game.lastAngle = b.angle;
      b.x += b.vx; b.y += b.vy;
      if (b.x < 30) { b.x = 30; b.vx = 0; }
      for (const c of game.coins) {
        if (!c.collected && Math.hypot(b.x - c.x, b.y - c.y) < 28) {
          c.collected = true; game.coinsCollected++; game.score += 100; addP(c.x, c.y, 8, '#FFD700');
        }
      }
      if (b.x >= game.finishX && !game.finished) endGame('finished');
      game.cam.x += (b.x - W * 0.35 - game.cam.x) * 0.08;
      game.cam.y += (b.y - H * 0.55 - game.cam.y) * 0.06;
      game.particles = game.particles.filter(p => { p.x += p.vx; p.y += p.vy; p.vy += 0.08; p.life -= 0.016; return p.life > 0; });
    }

    // ── DRAW SKY (desert) ──
    function drawSky() {
      const g = ctx.createLinearGradient(0, 0, 0, H * 0.65);
      g.addColorStop(0, '#6BB3D9'); g.addColorStop(0.4, '#A8D0E6');
      g.addColorStop(0.75, '#E8C9A0'); g.addColorStop(1, '#E8956A');
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      // Sun
      const sx = W * 0.78, sy = H * 0.18;
      ctx.save();
      ctx.globalAlpha = 0.08; ctx.beginPath(); ctx.arc(sx, sy, 80, 0, Math.PI * 2); ctx.fillStyle = '#FFD54F'; ctx.fill();
      ctx.globalAlpha = 0.04; ctx.beginPath(); ctx.arc(sx, sy, 120, 0, Math.PI * 2); ctx.fill();
      ctx.globalAlpha = 1;
      const sg = ctx.createRadialGradient(sx, sy, 0, sx, sy, 28);
      sg.addColorStop(0, '#FFF9C4'); sg.addColorStop(1, '#FFD54F');
      ctx.beginPath(); ctx.arc(sx, sy, 28, 0, Math.PI * 2); ctx.fillStyle = sg; ctx.fill();
      ctx.restore();
    }
    function drawClouds() {
      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      for (const cl of CLOUDS) {
        let cx2 = ((cl.x - game.cam.x * 0.03) % (W + 400)) - 120;
        if (cx2 < -130 || cx2 > W + 130) continue;
        ctx.save(); ctx.translate(cx2, cl.y); ctx.scale(cl.s, cl.s);
        for (const [px, py, pr] of [[-14, 4, 14], [0, 6, 17], [16, 3, 13], [-7, -4, 15], [9, -3, 13], [0, -8, 11]]) {
          ctx.beginPath(); ctx.arc(px, py, pr, 0, Math.PI * 2); ctx.fill();
        }
        ctx.restore();
      }
    }
    // ── DRAW MOUNTAINS (desert mesas) ──
    function drawMountains() {
      const layers = [
        { sp: 0.012, c: ['#C9A882', '#B89872'], h: 0.52, a: [0.002, 0.006, 0.001] },
        { sp: 0.035, c: ['#A67B5B', '#956A4A'], h: 0.45, a: [0.003, 0.008, 0.002] },
        { sp: 0.07, c: ['#7D4E2A', '#6B3E1F'], h: 0.38, a: [0.004, 0.01, 0.003] },
      ];
      for (const l of layers) {
        const off = game.cam.x * l.sp, by = H * l.h;
        ctx.beginPath(); ctx.moveTo(-5, H);
        for (let i = -5; i <= W + 5; i += 3) {
          const wx = i + off;
          // Mesa-like shapes: more angular/flat-topped
          const raw = Math.sin(wx * l.a[0]) * 50 + Math.sin(wx * l.a[1] + 1) * 28 + Math.cos(wx * l.a[2]) * 40;
          const mh = Math.max(raw, raw * 0.3 + 20) * (1 - l.h + 0.5);
          ctx.lineTo(i, by - Math.max(mh, 0));
        }
        ctx.lineTo(W + 5, H); ctx.closePath();
        const mg = ctx.createLinearGradient(0, by - 70, 0, by + 20);
        mg.addColorStop(0, l.c[0]); mg.addColorStop(1, l.c[1]);
        ctx.fillStyle = mg; ctx.fill();
      }
    }
    // ── DRAW TERRAIN (desert rock) ──
    function drawTerrain() {
      const t = game.terrain, cx = game.cam.x, cy = game.cam.y;
      ctx.beginPath();
      ctx.moveTo(t[0].x - cx, H + 20);
      for (const p of t) ctx.lineTo(p.x - cx, p.y - cy);
      ctx.lineTo(t[t.length - 1].x - cx, H + 20); ctx.closePath();
      const tg = ctx.createLinearGradient(0, 160, 0, H);
      tg.addColorStop(0, '#C8A882'); tg.addColorStop(0.15, '#A0704A');
      tg.addColorStop(0.4, '#7D5535'); tg.addColorStop(1, '#4A2810');
      ctx.fillStyle = tg; ctx.fill();
      // Top sandy edge
      ctx.beginPath();
      for (let i = 0; i < t.length; i++) {
        const px = t[i].x - cx, py = t[i].y - cy;
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      for (let i = t.length - 1; i >= 0; i--) ctx.lineTo(t[i].x - cx, t[i].y - cy + 4);
      ctx.closePath();
      ctx.fillStyle = '#D4B896'; ctx.fill();
      // Surface rocks
      for (const r of game.surfaceRocks) {
        const rx = r.x - cx; if (rx < -20 || rx > W + 20) continue;
        ctx.beginPath(); ctx.arc(rx, r.y - cy + 2 + r.r * 0.5, r.r, 0, Math.PI * 2);
        ctx.fillStyle = r.shade > 0 ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)'; ctx.fill();
      }
    }
    // ── DRAW CACTI ──
    function drawCacti() {
      for (const c of game.cacti) {
        const cx2 = c.x - game.cam.x; if (cx2 < -30 || cx2 > W + 30) continue;
        const cy2 = c.y - game.cam.y;
        ctx.save(); ctx.translate(cx2, cy2); ctx.scale(c.size, c.size);
        if (c.type === 'tall') {
          ctx.fillStyle = '#5A8A3A'; ctx.strokeStyle = '#4A7A2E'; ctx.lineWidth = 0.8;
          ctx.fillRect(-2.5, -28, 5, 28); ctx.strokeRect(-2.5, -28, 5, 28);
          ctx.fillRect(-10, -20, 7.5, 3); ctx.fillRect(-10, -20, 3, 10);
          ctx.fillRect(2.5, -16, 7.5, 3); ctx.fillRect(7, -16, 3, 8);
        } else {
          ctx.fillStyle = '#5A8A3A'; ctx.strokeStyle = '#4A7A2E'; ctx.lineWidth = 0.8;
          ctx.beginPath(); ctx.ellipse(0, -7, 4.5, 7, 0, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
        }
        ctx.restore();
      }
    }
    // ── DRAW COINS ──
    function drawCoins() {
      for (const c of game.coins) {
        if (c.collected) continue;
        const cx2 = c.x - game.cam.x, cy2 = c.y - game.cam.y;
        if (cx2 < -20 || cx2 > W + 20) continue;
        ctx.save();
        ctx.globalAlpha = 0.12; ctx.beginPath(); ctx.arc(cx2, cy2, 14, 0, Math.PI * 2); ctx.fillStyle = '#FFD700'; ctx.fill();
        ctx.globalAlpha = 1;
        ctx.beginPath(); ctx.arc(cx2, cy2, 10, 0, Math.PI * 2); ctx.fillStyle = '#FFD700'; ctx.fill();
        ctx.strokeStyle = '#DAA520'; ctx.lineWidth = 1.5; ctx.stroke();
        ctx.fillStyle = '#B8860B'; ctx.font = 'bold 9px Oswald,sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText('$', cx2, cy2 + 0.5);
        ctx.restore();
      }
    }
    // ── DRAW FINISH ──
    function drawFinish() {
      const fx = game.finishX - game.cam.x; if (fx < -80 || fx > W + 150) return;
      const fy = getTerrainYAt(game.terrain, game.finishX) - game.cam.y;
      ctx.fillStyle = '#6B3A1F'; ctx.fillRect(fx, fy - 75, 3, 75);
      const cs = 5;
      for (let r = 0; r < 5; r++) for (let c2 = 0; c2 < 6; c2++) {
        ctx.fillStyle = (r + c2) % 2 === 0 ? '#fff' : '#111';
        ctx.fillRect(fx + 3 + c2 * cs, fy - 75 + r * cs, cs, cs);
      }
    }
    // ── DRAW MOTORCYCLE ──
    function drawBike() {
      const b = game.bike, sx = b.x - game.cam.x, sy = b.y - game.cam.y;
      const wr = b.x * 0.07;
      ctx.save(); ctx.translate(sx, sy); ctx.rotate(b.angle);
      const WR = 16, bwx = -20, fwx = 22, wy = 3;
      function drawWheel(cx, cy) {
        ctx.beginPath(); ctx.arc(cx, cy, WR, 0, Math.PI * 2); ctx.fillStyle = '#1A1A1A'; ctx.fill();
        ctx.strokeStyle = '#111'; ctx.lineWidth = 3; ctx.stroke();
        ctx.strokeStyle = '#2A2A2A'; ctx.lineWidth = 1;
        for (let a = 0; a < 8; a++) { const an = wr + a * Math.PI / 4; ctx.beginPath(); ctx.moveTo(cx + Math.cos(an) * WR * 0.85, cy + Math.sin(an) * WR * 0.85); ctx.lineTo(cx + Math.cos(an) * WR * 1.05, cy + Math.sin(an) * WR * 1.05); ctx.stroke(); }
        ctx.beginPath(); ctx.arc(cx, cy, WR * 0.58, 0, Math.PI * 2); ctx.fillStyle = '#777'; ctx.fill();
        ctx.beginPath(); ctx.arc(cx, cy, 2.5, 0, Math.PI * 2); ctx.fillStyle = '#aaa'; ctx.fill();
        ctx.strokeStyle = '#bbb'; ctx.lineWidth = 0.7;
        for (let a = 0; a < 8; a++) { const an = wr + a * Math.PI / 4; ctx.beginPath(); ctx.moveTo(cx + Math.cos(an) * 2.5, cy + Math.sin(an) * 2.5); ctx.lineTo(cx + Math.cos(an) * WR * 0.53, cy + Math.sin(an) * WR * 0.53); ctx.stroke(); }
      }
      drawWheel(bwx, wy); drawWheel(fwx, wy);
      // Swing arm
      ctx.strokeStyle = '#555'; ctx.lineWidth = 2.8; ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(-4, -6); ctx.lineTo(bwx, wy); ctx.stroke();
      // Fork
      ctx.strokeStyle = '#999'; ctx.lineWidth = 2.2;
      ctx.beginPath(); ctx.moveTo(14, -21); ctx.lineTo(fwx - 1, wy - 2); ctx.stroke();
      ctx.strokeStyle = '#C0C0C0'; ctx.lineWidth = 1.8;
      ctx.beginPath(); ctx.moveTo(16, -21); ctx.lineTo(fwx + 1, wy - 2); ctx.stroke();
      // Frame
      ctx.strokeStyle = '#CC2200'; ctx.lineWidth = 3.5; ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(-7, -3); ctx.lineTo(-5, -21); ctx.lineTo(14, -21); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(-5, -21); ctx.lineTo(3, -3); ctx.lineTo(14, -21); ctx.stroke();
      // Engine
      ctx.fillStyle = '#444';
      ctx.beginPath(); ctx.moveTo(-5, -3); ctx.lineTo(7, -3); ctx.lineTo(9, -13); ctx.lineTo(-3, -13); ctx.closePath(); ctx.fill();
      ctx.strokeStyle = '#555'; ctx.lineWidth = 0.7;
      for (let i = 0; i < 3; i++) { ctx.beginPath(); ctx.moveTo(-5, -5 - i * 3); ctx.lineTo(9, -5 - i * 3); ctx.stroke(); }
      // Exhaust
      ctx.strokeStyle = '#888'; ctx.lineWidth = 2.8; ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(-3, -3); ctx.lineTo(-13, 1); ctx.lineTo(-25, 0); ctx.stroke();
      ctx.strokeStyle = '#999'; ctx.lineWidth = 3.5;
      ctx.beginPath(); ctx.moveTo(-25, 0); ctx.lineTo(-31, 1); ctx.stroke();
      // Tank
      ctx.fillStyle = '#CC2200';
      ctx.beginPath(); ctx.moveTo(-1, -23); ctx.quadraticCurveTo(5, -29, 11, -23); ctx.lineTo(11, -19); ctx.quadraticCurveTo(5, -17, -1, -19); ctx.closePath(); ctx.fill();
      ctx.strokeStyle = '#AA1800'; ctx.lineWidth = 0.6; ctx.stroke();
      ctx.strokeStyle = '#FF6633'; ctx.lineWidth = 0.8;
      ctx.beginPath(); ctx.moveTo(1, -23); ctx.quadraticCurveTo(5, -26, 9, -23); ctx.stroke();
      // Seat
      ctx.fillStyle = '#2A1A0A';
      ctx.beginPath(); ctx.moveTo(-9, -19); ctx.quadraticCurveTo(-5, -23, -1, -21); ctx.lineTo(-1, -18); ctx.quadraticCurveTo(-5, -16, -9, -18); ctx.closePath(); ctx.fill();
      // Handlebars
      ctx.strokeStyle = '#555'; ctx.lineWidth = 2.2;
      ctx.beginPath(); ctx.moveTo(13, -23); ctx.lineTo(11, -29); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(16, -23); ctx.lineTo(18, -29); ctx.stroke();
      // Headlight
      ctx.fillStyle = '#FFF9C4'; ctx.beginPath(); ctx.ellipse(fwx + 2, -11, 2.5, 3.5, 0.2, 0, Math.PI * 2); ctx.fill();
      // Taillight
      ctx.fillStyle = '#F44336'; ctx.beginPath(); ctx.ellipse(-11, -18, 1.8, 1.3, 0, 0, Math.PI * 2); ctx.fill();
      // RIDER
      ctx.lineCap = 'round';
      ctx.fillStyle = '#222'; ctx.fillRect(-13, -1, 4, 2.5); ctx.fillRect(9, -1, 4, 2.5);
      ctx.strokeStyle = '#2C3E50'; ctx.lineWidth = 3.5;
      ctx.beginPath(); ctx.moveTo(-5, -21); ctx.lineTo(-10, -8); ctx.lineTo(-11, 0); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(-1, -21); ctx.lineTo(6, -8); ctx.lineTo(11, 0); ctx.stroke();
      ctx.fillStyle = '#1A2530';
      ctx.beginPath(); ctx.moveTo(-7, -22); ctx.lineTo(3, -22); ctx.lineTo(5, -38); ctx.lineTo(-5, -38); ctx.closePath(); ctx.fill();
      ctx.strokeStyle = '#1A2530'; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.moveTo(1, -35); ctx.lineTo(9, -28); ctx.lineTo(12, -29); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(2, -36); ctx.lineTo(11, -28); ctx.lineTo(17, -29); ctx.stroke();
      ctx.fillStyle = '#222'; ctx.beginPath(); ctx.arc(12, -29, 1.8, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(17, -29, 1.8, 0, Math.PI * 2); ctx.fill();
      // Helmet
      ctx.fillStyle = '#1565C0'; ctx.beginPath(); ctx.arc(0, -46, 8.5, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = '#0D47A1'; ctx.lineWidth = 1.3; ctx.stroke();
      ctx.beginPath(); ctx.moveTo(5, -43); ctx.quadraticCurveTo(10, -44, 9, -48); ctx.strokeStyle = '#1565C0'; ctx.lineWidth = 2.5; ctx.stroke();
      const vg = ctx.createLinearGradient(3, -50, 10, -44);
      vg.addColorStop(0, '#64FFDA'); vg.addColorStop(1, '#00BFA5');
      ctx.fillStyle = vg;
      ctx.beginPath(); ctx.moveTo(5, -50); ctx.lineTo(9, -48); ctx.lineTo(9, -44); ctx.lineTo(5, -43); ctx.closePath(); ctx.fill();
      ctx.strokeStyle = '#FFD54F'; ctx.lineWidth = 0.8;
      ctx.beginPath(); ctx.arc(0, -46, 9, -2.4, -0.7); ctx.stroke();
      ctx.restore();
    }
    function drawParticles() {
      for (const p of game.particles) {
        ctx.globalAlpha = p.life; ctx.beginPath();
        ctx.arc(p.x - game.cam.x, p.y - game.cam.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color; ctx.fill();
      }
      ctx.globalAlpha = 1;
    }
    // ── HUD ──
    function drawHUD() {
      // Coins
      ctx.fillStyle = 'rgba(0,0,0,0.45)'; ctx.beginPath(); ctx.roundRect(10, 10, 95, 26, 5); ctx.fill();
      ctx.beginPath(); ctx.arc(22, 23, 7, 0, Math.PI * 2); ctx.fillStyle = '#FFD700'; ctx.fill();
      ctx.fillStyle = '#B8860B'; ctx.font = 'bold 7px Oswald'; ctx.textAlign = 'center'; ctx.fillText('$', 22, 25);
      ctx.fillStyle = '#FFD700'; ctx.font = 'bold 13px Oswald,sans-serif'; ctx.textAlign = 'left'; ctx.fillText(`${game.coinsCollected}/50`, 34, 28);
      // Level
      ctx.fillStyle = 'rgba(0,0,0,0.45)'; ctx.beginPath(); ctx.roundRect(W - 80, 10, 70, 22, 5); ctx.fill();
      ctx.fillStyle = '#fff'; ctx.font = 'bold 11px Oswald,sans-serif'; ctx.textAlign = 'right'; ctx.fillText(`LEVEL ${level}`, W - 16, 26);
      // Flip bar
      if (!game.bike.onGround && Math.abs(game.totalRotation) > 0.5) {
        const pct = Math.min(Math.abs(game.totalRotation) / (Math.PI * 2), 1);
        ctx.fillStyle = 'rgba(0,0,0,0.45)'; ctx.beginPath(); ctx.roundRect(W / 2 - 40, 10, 80, 18, 4); ctx.fill();
        ctx.fillStyle = pct > 0.7 ? '#66BB6A' : '#FFD54F'; ctx.fillRect(W / 2 - 38, 12, 76 * pct, 14);
        ctx.fillStyle = '#fff'; ctx.font = 'bold 9px Oswald'; ctx.textAlign = 'center'; ctx.fillText('FLIP!', W / 2, 24);
      }
    }
    function drawCountdown() {
      if (game.countdown < 0) return;
      ctx.fillStyle = 'rgba(0,0,0,0.5)'; ctx.fillRect(0, 0, W, H);
      const pulse = 1 + Math.sin(game.countdownTimer * Math.PI) * 0.12;
      if (!game.countdownStarted) {
        ctx.fillStyle = '#C8A882'; ctx.font = 'bold 20px Oswald,sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText('Indlæser bane...', W / 2, H / 2); return;
      }
      ctx.save(); ctx.translate(W / 2, H / 2 - 20); ctx.scale(pulse, pulse);
      if (game.countdown > 0) {
        const cols = ['', '#EF5350', '#FFD54F', '#42A5F5'];
        ctx.fillStyle = cols[game.countdown] || '#42A5F5';
        ctx.font = 'bold 80px Oswald,sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.shadowColor = ctx.fillStyle; ctx.shadowBlur = 22;
        ctx.fillText(game.countdown.toString(), 0, 0);
      } else {
        ctx.fillStyle = '#66BB6A'; ctx.font = 'bold 64px Oswald,sans-serif';
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.shadowColor = '#66BB6A'; ctx.shadowBlur = 30;
        ctx.fillText('KØR!', 0, 0);
      }
      ctx.restore(); ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0;
      ctx.fillStyle = '#C8A882'; ctx.font = '13px Oswald,sans-serif'; ctx.textAlign = 'center';
      ctx.fillText(`Level ${level} — Saml 50 mønter og nå målstregen`, W / 2, H / 2 + 50);
    }
    function draw() {
      ctx.clearRect(0, 0, W, H);
      drawSky(); drawClouds(); drawMountains(); drawTerrain(); drawCacti(); drawCoins(); drawFinish(); drawBike(); drawParticles();
      if (game.countdown >= 0) drawCountdown(); else drawHUD();
    }
    function loop() { update(); draw(); frameRef.current = requestAnimationFrame(loop); }
    frameRef.current = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(frameRef.current); };
  }, [level, onFinish]);

  const handleKey = useCallback((key, val) => { keysRef.current[key] = val; }, []);
  useEffect(() => {
    const kd = (e) => {
      if (e.key === 'a' || e.key === 'q') keysRef.current.rotBack = true;
      if (e.key === 'd' || e.key === 'e') keysRef.current.rotFwd = true;
      if (e.key === 'ArrowLeft') keysRef.current.brake = true;
      if (e.key === 'ArrowRight' || e.key === 'ArrowUp') keysRef.current.gas = true;
    };
    const ku = (e) => {
      if (e.key === 'a' || e.key === 'q') keysRef.current.rotBack = false;
      if (e.key === 'd' || e.key === 'e') keysRef.current.rotFwd = false;
      if (e.key === 'ArrowLeft') keysRef.current.brake = false;
      if (e.key === 'ArrowRight' || e.key === 'ArrowUp') keysRef.current.gas = false;
    };
    window.addEventListener('keydown', kd); window.addEventListener('keyup', ku);
    return () => { window.removeEventListener('keydown', kd); window.removeEventListener('keyup', ku); };
  }, []);

  const togglePause = () => { pausedRef.current = !pausedRef.current; setPaused(!paused); };
  const handleResult = () => {
    const g = gameRef.current; if (!g) return;
    onFinish({ level, score: g.score, coins_collected: g.coinsCollected, flips: g.flips, time_seconds: g.time, finished: g.finished, crashed: g.crashed });
  };

  const btnClass = "w-[56px] h-[56px] sm:w-[62px] sm:h-[62px] rounded-full bg-[#1a1510]/80 border-[3px] border-[#9B8767]/70 flex items-center justify-center text-white/90 active:bg-[#2a1f14] active:scale-95 select-none shadow-lg shadow-black/30";

  return (
    <div className="relative w-full h-full" style={{ touchAction: 'none' }} data-testid="game-canvas-container">
      <canvas ref={canvasRef} width={800} height={450} className="w-full h-full" style={{ touchAction: 'none' }} data-testid="game-canvas" />

      {/* Pause btn */}
      <button data-testid="pause-btn" onClick={togglePause}
        className="absolute top-2 right-2 w-8 h-8 bg-black/40 border border-[#9B8767]/40 rounded-lg flex items-center justify-center text-white/70 z-10">
        <Pause size={14} />
      </button>
      {/* Back btn */}
      <button data-testid="back-btn" onClick={onBack}
        className="absolute top-2 left-2 w-8 h-8 bg-black/40 border border-[#9B8767]/40 rounded-lg flex items-center justify-center text-white/70 z-10">
        <ArrowLeft size={14} />
      </button>

      {/* Controls - only show when playing */}
      {!endState && !paused && (
        <>
          {/* LEFT: Rotation */}
          <div className="absolute bottom-4 left-3 flex gap-2.5 z-10">
            <button data-testid="ctrl-rot-back" className={btnClass}
              onTouchStart={(e) => { e.preventDefault(); handleKey('rotBack', true); }}
              onTouchEnd={(e) => { e.preventDefault(); handleKey('rotBack', false); }}
              onTouchCancel={() => handleKey('rotBack', false)}
              onMouseDown={() => handleKey('rotBack', true)} onMouseUp={() => handleKey('rotBack', false)} onMouseLeave={() => handleKey('rotBack', false)}>
              <Undo2 size={22} />
            </button>
            <button data-testid="ctrl-rot-fwd" className={btnClass}
              onTouchStart={(e) => { e.preventDefault(); handleKey('rotFwd', true); }}
              onTouchEnd={(e) => { e.preventDefault(); handleKey('rotFwd', false); }}
              onTouchCancel={() => handleKey('rotFwd', false)}
              onMouseDown={() => handleKey('rotFwd', true)} onMouseUp={() => handleKey('rotFwd', false)} onMouseLeave={() => handleKey('rotFwd', false)}>
              <Redo2 size={22} />
            </button>
          </div>
          {/* RIGHT: Speed */}
          <div className="absolute bottom-4 right-3 flex gap-2.5 z-10">
            <button data-testid="ctrl-brake" className={btnClass}
              onTouchStart={(e) => { e.preventDefault(); handleKey('brake', true); }}
              onTouchEnd={(e) => { e.preventDefault(); handleKey('brake', false); }}
              onTouchCancel={() => handleKey('brake', false)}
              onMouseDown={() => handleKey('brake', true)} onMouseUp={() => handleKey('brake', false)} onMouseLeave={() => handleKey('brake', false)}>
              <ChevronLeft size={24} />
            </button>
            <button data-testid="ctrl-gas" className={btnClass}
              onTouchStart={(e) => { e.preventDefault(); handleKey('gas', true); }}
              onTouchEnd={(e) => { e.preventDefault(); handleKey('gas', false); }}
              onTouchCancel={() => handleKey('gas', false)}
              onMouseDown={() => handleKey('gas', true)} onMouseUp={() => handleKey('gas', false)} onMouseLeave={() => handleKey('gas', false)}>
              <ChevronRight size={24} />
            </button>
          </div>
        </>
      )}

      {/* PAUSE overlay */}
      {paused && !endState && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/60" data-testid="pause-overlay">
          <p className="text-white font-['Oswald'] text-3xl font-bold tracking-wider mb-6">PAUSE</p>
          <button onClick={togglePause} data-testid="resume-btn"
            className="px-8 py-3 bg-[#CC2200] hover:bg-[#E53935] text-white font-['Oswald'] font-bold text-lg uppercase tracking-wider rounded-xl mb-3 flex items-center gap-2">
            <Play size={18} /> Fortsæt
          </button>
          <button onClick={() => { pausedRef.current = false; handleResult(); }} data-testid="quit-btn"
            className="px-8 py-2.5 bg-white/10 hover:bg-white/20 text-white/70 font-['Oswald'] text-sm uppercase tracking-wider rounded-xl flex items-center gap-2">
            <X size={14} /> Afslut
          </button>
        </div>
      )}

      {/* CRASH overlay */}
      {endState?.type === 'crashed' && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/55" data-testid="crash-overlay">
          <p className="text-red-400 font-['Oswald'] text-3xl font-bold uppercase tracking-wider mb-1">Crashed!</p>
          <p className="text-gray-400 text-sm font-['Oswald'] mb-5">
            {endState.crashType === 'wheelie' ? 'Du faldt bagover!' : endState.crashType === 'stoppie' ? 'Du faldt forover!' : endState.crashType === 'head' ? 'Hjelmen ramte jorden!' : 'Dårlig landing!'}
          </p>
          <button data-testid="restart-btn" onClick={handleResult}
            className="px-8 py-3 bg-[#CC2200] hover:bg-[#E53935] text-white font-['Oswald'] font-bold text-lg uppercase tracking-wider rounded-xl flex items-center gap-2">
            <RefreshCw size={18} /> Prøv Igen
          </button>
        </div>
      )}

      {/* FINISH overlay */}
      {endState?.type === 'finished' && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/60" data-testid="finish-overlay">
          <p className="text-[#FFD700] font-['Oswald'] text-3xl font-bold uppercase tracking-wider mb-2">Level Gennemført!</p>
          <div className="flex gap-6 mb-4 text-center">
            <div><p className="text-[#FFD700] font-['Oswald'] text-xl font-bold">{endState.score}</p><p className="text-gray-400 text-xs">Score</p></div>
            <div><p className="text-white font-['Oswald'] text-xl font-bold">{endState.coins}/50</p><p className="text-gray-400 text-xs">Mønter</p></div>
            <div><p className="text-cyan-400 font-['Oswald'] text-xl font-bold">{endState.flips}</p><p className="text-gray-400 text-xs">Flips</p></div>
          </div>
          <button data-testid="continue-btn" onClick={handleResult}
            className="px-8 py-3 bg-[#FFD700] hover:bg-yellow-400 text-black font-['Oswald'] font-bold text-lg uppercase tracking-wider rounded-xl flex items-center gap-2">
            <Play size={18} /> Fortsæt
          </button>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════
//  LEVEL SELECT (with locking)
// ═══════════════════════════════════════
const LevelSelect = ({ levels, onSelect, onBack }) => {
  const levelColors = ['#CC6633', '#AA5522', '#885522', '#774422', '#663311'];
  return (
    <div className="w-full min-h-screen flex flex-col bg-gradient-to-b from-[#1a0e05] to-[#2a1a0a] pt-20 px-4 pb-8" data-testid="level-select-screen">
      <div className="flex items-center gap-3 mb-6 max-w-md mx-auto w-full">
        <button onClick={onBack} className="w-8 h-8 bg-black/40 border border-[#9B8767]/30 rounded-lg flex items-center justify-center text-white"><ArrowLeft size={16} /></button>
        <h2 className="text-2xl font-['Oswald'] font-bold text-[#D4A574] uppercase tracking-wider">Vælg Level</h2>
      </div>
      <div className="flex-1 flex flex-col gap-3 justify-center max-w-md mx-auto w-full">
        {levels.map((lvl, i) => (
          <button key={lvl.level} data-testid={`level-btn-${lvl.level}`}
            onClick={() => lvl.unlocked && onSelect(lvl.level)}
            disabled={!lvl.unlocked}
            className={`relative flex items-center gap-4 p-3 rounded-xl border transition-all ${lvl.unlocked ? 'bg-[#2a1a0a]/60 border-[#9B8767]/30 hover:border-[#9B8767]/60 hover:bg-[#2a1a0a]/80 cursor-pointer' : 'bg-[#1a0e05]/40 border-[#4a3a2a]/20 opacity-40 cursor-not-allowed'}`}>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg font-['Oswald'] flex-shrink-0" style={{ backgroundColor: lvl.unlocked ? levelColors[i] : '#333' }}>
              {lvl.unlocked ? lvl.level : <Lock size={18} />}
            </div>
            <div className="flex-1 text-left">
              <p className="text-white font-['Oswald'] font-medium">{lvl.name}</p>
              <p className="text-[#9B8767] text-xs">50 mønter</p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <div className="flex gap-0.5">{[1, 2, 3].map(s => (<Star key={s} size={14} className={s <= lvl.stars ? 'text-[#FFD700] fill-[#FFD700]' : 'text-[#4a3a2a]'} />))}</div>
              {lvl.best_score > 0 && <p className="text-[#9B8767] text-xs">{lvl.best_score} pts</p>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════
//  LEADERBOARD
// ═══════════════════════════════════════
const Leaderboard = ({ onBack }) => {
  const [data, setData] = useState([]); const [loading, setLoading] = useState(true);
  const { member } = useAuth();
  useEffect(() => { axios.get(`${API}/moto-game/leaderboard`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  const medals = ['', '#FFD700', '#C0C0C0', '#CD7F32'];
  return (
    <div className="w-full min-h-screen flex flex-col bg-gradient-to-b from-[#1a0e05] to-[#2a1a0a] pt-20 px-4 pb-8 max-w-lg mx-auto" data-testid="leaderboard-screen">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={onBack} className="w-8 h-8 bg-black/40 border border-[#9B8767]/30 rounded-lg flex items-center justify-center text-white"><ArrowLeft size={16} /></button>
        <h2 className="text-xl font-['Oswald'] font-bold text-[#FFD700] uppercase tracking-wider flex items-center gap-2"><Trophy size={20} /> High Score</h2>
      </div>
      <div className="bg-gradient-to-r from-[#FFD700]/10 to-transparent border border-[#FFD700]/20 rounded-lg p-3 mb-4">
        <p className="text-[#FFD700] text-sm font-['Oswald']">Nr. 1 den 1. i hver måned vinder en bar mønt!</p>
      </div>
      {loading ? <p className="text-[#9B8767] text-center py-8">Indlæser...</p> : data.length === 0 ? <p className="text-[#9B8767] text-center py-8">Ingen scores endnu</p> : (
        <div className="space-y-1.5 flex-1 overflow-auto">
          {data.map(e => (
            <div key={e.rank} data-testid={`leaderboard-rank-${e.rank}`}
              className={`flex items-center gap-3 p-2.5 rounded-lg ${e.rank <= 3 ? 'bg-[#2a1a0a]/60 border border-[#9B8767]/20' : 'bg-[#1a0e05]/40'} ${e.member_name === member?.name ? 'ring-1 ring-[#FFD700]/40' : ''}`}>
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                {e.rank <= 3 ? <Medal size={22} style={{ color: medals[e.rank] }} /> : <span className="text-[#9B8767] font-['Oswald'] font-bold text-sm">{e.rank}</span>}
              </div>
              <div className="flex-1 min-w-0"><p className={`font-['Oswald'] font-medium truncate ${e.rank <= 3 ? 'text-white' : 'text-gray-300'}`}>{e.member_name}</p></div>
              <div className="text-right flex-shrink-0">
                <p className="text-[#FFD700] font-['Oswald'] font-bold">{e.total_score.toLocaleString()}</p>
                <p className="text-[#9B8767] text-xs">{e.total_coins} mønter</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════
//  LOBBY (desert themed)
// ═══════════════════════════════════════
const GameLobby = ({ onPlay, onLeaderboard, onMyStats }) => (
  <div className="min-h-screen bg-gradient-to-b from-[#1a0e05] via-[#2a1a0a] to-[#1a0e05] flex flex-col items-center pt-20 px-4 pb-8" data-testid="game-lobby">
    <div className="text-center mb-8">
      <h1 className="text-4xl sm:text-5xl font-['Oswald'] font-black text-white uppercase tracking-wider">Moto <span className="text-[#CC2200]">Rider</span></h1>
      <p className="text-[#9B8767] text-sm font-['Oswald'] mt-1 tracking-widest uppercase">The Others MC</p>
    </div>
    <div className="relative w-28 h-28 mb-8">
      <div className="absolute inset-0 bg-[#CC2200]/10 rounded-full animate-pulse" />
      <div className="absolute inset-3 bg-[#CC2200]/5 rounded-full border border-[#CC2200]/20 flex items-center justify-center"><Bike size={44} className="text-[#CC2200]" /></div>
    </div>
    <div className="flex flex-col gap-3 w-full max-w-xs">
      <button data-testid="play-game-btn" onClick={onPlay}
        className="w-full py-4 bg-gradient-to-r from-[#CC2200] to-[#AA1800] text-white font-['Oswald'] font-bold text-xl uppercase tracking-wider rounded-xl hover:from-[#E53935] hover:to-[#CC2200] transition-all active:scale-95 flex items-center justify-center gap-3 shadow-lg shadow-red-900/30">
        <Play size={24} fill="white" /> Spil Nu
      </button>
      <button data-testid="lobby-leaderboard-btn" onClick={onLeaderboard}
        className="w-full py-3 bg-[#2a1a0a]/60 border border-[#FFD700]/30 text-[#FFD700] font-['Oswald'] font-medium text-base uppercase tracking-wider rounded-xl hover:bg-[#FFD700]/10 transition-all flex items-center justify-center gap-3">
        <Trophy size={20} /> High Score
      </button>
      <button data-testid="lobby-mystats-btn" onClick={onMyStats}
        className="w-full py-3 bg-[#2a1a0a]/60 border border-[#9B8767]/30 text-[#9B8767] font-['Oswald'] font-medium text-base uppercase tracking-wider rounded-xl hover:bg-white/5 transition-all flex items-center justify-center gap-3">
        <Medal size={20} /> Mine Resultater
      </button>
    </div>
    <div className="mt-8 bg-gradient-to-r from-[#FFD700]/5 to-transparent border border-[#FFD700]/10 rounded-xl p-4 max-w-xs w-full text-center">
      <p className="text-[#FFD700] text-xs font-['Oswald'] uppercase tracking-wider">Nr. 1 den 1. i hver måned vinder en bar mønt!</p>
    </div>
  </div>
);

// ═══════════════════════════════════════
//  MY STATS
// ═══════════════════════════════════════
const MyStats = ({ stats, levels, onBack }) => (
  <div className="min-h-screen bg-gradient-to-b from-[#1a0e05] to-[#2a1a0a] pt-20 px-4 pb-8 max-w-lg mx-auto" data-testid="mystats-screen">
    <div className="flex items-center gap-3 mb-6">
      <button onClick={onBack} className="w-8 h-8 bg-black/40 border border-[#9B8767]/30 rounded-lg flex items-center justify-center text-white"><ArrowLeft size={16} /></button>
      <h2 className="text-xl font-['Oswald'] font-bold text-white uppercase tracking-wider">Mine Resultater</h2>
    </div>
    {stats.length === 0 ? <p className="text-[#9B8767] text-center py-12 font-['Oswald']">Ingen resultater endnu</p> : (
      <div className="space-y-3">
        {levels.map(lvl => {
          const s = stats.find(st => st.level === lvl.level);
          return (
            <div key={lvl.level} className={`p-4 rounded-xl border ${s ? 'bg-[#2a1a0a]/60 border-[#9B8767]/20' : 'bg-[#1a0e05]/30 border-[#4a3a2a]/20 opacity-40'}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-white font-['Oswald'] font-medium">{lvl.level}. {lvl.name}</p>
                <div className="flex gap-0.5">{[1, 2, 3].map(star => (<Star key={star} size={14} className={star <= (lvl.stars || 0) ? 'text-[#FFD700] fill-[#FFD700]' : 'text-[#4a3a2a]'} />))}</div>
              </div>
              {s ? (
                <div className="flex gap-4 text-sm">
                  <span className="text-[#FFD700]">{s.best_score} pts</span>
                  <span className="text-[#9B8767]">{s.best_coins} mønter</span>
                  <span className="text-cyan-400">{s.total_flips} flips</span>
                  <span className="text-[#6B5B4A]">{s.attempts}x</span>
                </div>
              ) : <p className="text-[#6B5B4A] text-sm">Låst</p>}
            </div>
          );
        })}
      </div>
    )}
  </div>
);

// ═══════════════════════════════════════
//  MAIN PAGE
// ═══════════════════════════════════════
const MotoGamePage = () => {
  const { member, getToken } = useAuth();
  const [screen, setScreen] = useState('lobby');
  const [levels, setLevels] = useState([]);
  const [myStats, setMyStats] = useState([]);
  const [currentLevel, setCurrentLevel] = useState(null);
  const [isLandscape, setIsLandscape] = useState(window.innerWidth > window.innerHeight);
  const getAuthHeaders = useCallback(() => ({ Authorization: `Bearer ${getToken()}` }), [getToken]);

  useEffect(() => {
    const check = () => setIsLandscape(window.innerWidth > window.innerHeight);
    window.addEventListener('resize', check);
    const onOrient = () => { setTimeout(check, 100); setTimeout(check, 300); setTimeout(check, 600); };
    window.addEventListener('orientationchange', onOrient);
    return () => { window.removeEventListener('resize', check); window.removeEventListener('orientationchange', onOrient); };
  }, []);

  const fetchLevels = useCallback(async () => {
    try { const r = await axios.get(`${API}/moto-game/levels`, { headers: getAuthHeaders() }); setLevels(r.data); }
    catch { toast.error('Kunne ikke hente levels'); }
  }, [getAuthHeaders]);
  const fetchMyStats = useCallback(async () => {
    try { const r = await axios.get(`${API}/moto-game/my-best`, { headers: getAuthHeaders() }); setMyStats(r.data); }
    catch {}
  }, [getAuthHeaders]);

  useEffect(() => { fetchLevels(); fetchMyStats(); }, [fetchLevels, fetchMyStats]);

  const handleFinish = useCallback(async (result) => {
    try {
      await axios.post(`${API}/moto-game/scores`, result, { headers: getAuthHeaders() });
      if (result.finished) toast.success(`Level gennemført! Score: ${result.score}`);
    } catch {}
    fetchLevels(); fetchMyStats();
    setScreen('levels'); setCurrentLevel(null);
  }, [getAuthHeaders, fetchLevels, fetchMyStats]);

  // Landscape check for game screen
  if (!isLandscape && screen === 'game') {
    return (
      <div className="fixed inset-0 bg-[#1a0e05] z-50 flex flex-col items-center justify-center text-white" data-testid="rotate-screen">
        <RotateCcw size={48} className="text-[#CC2200] mb-4 animate-spin" style={{ animationDuration: '3s' }} />
        <p className="text-lg font-['Oswald'] font-bold text-[#D4A574]">VEND DIN TELEFON</p>
        <p className="text-[#9B8767] text-sm mt-2">Spillet kræver landskab-mode</p>
      </div>
    );
  }

  return (
    <div className={`${screen === 'game' ? 'fixed inset-0 z-50 bg-black' : 'min-h-screen bg-[#1a0e05]'}`} data-testid="moto-game-page">
      {screen === 'lobby' && <GameLobby onPlay={() => setScreen('levels')} onLeaderboard={() => setScreen('leaderboard')} onMyStats={() => setScreen('mystats')} />}
      {screen === 'levels' && <LevelSelect levels={levels} onSelect={(l) => { setCurrentLevel(l); setScreen('game'); }} onBack={() => setScreen('lobby')} />}
      {screen === 'game' && currentLevel && <GameCanvas level={currentLevel} onFinish={handleFinish} onBack={() => { setScreen('levels'); setCurrentLevel(null); }} />}
      {screen === 'leaderboard' && <Leaderboard onBack={() => setScreen('lobby')} />}
      {screen === 'mystats' && <MyStats stats={myStats} levels={levels} onBack={() => setScreen('lobby')} />}
    </div>
  );
};

export default MotoGamePage;
